import { Checkbox, FormControlLabel } from "@mui/material";
import { styled } from "@mui/material/styles";
import get from "lodash/get";
import React, { useState } from "react";
import BSInput from "../../../common/js/components/BSInput";
import BSSelect from "../../../common/js/components/BSSelect";
import { accountingMethodTypes } from "../types/accountingMethodTypes";
import { accountingSoftwareSystemTypes } from "../types/accountingSoftwareSystemTypes";
import { dateOrdinalOptions } from "../utils/constants";
import {
    realEstateTaxesPaidByOptions,
    softwareLicenseReimbursedFrequencyOptions,
    taxOwnershipInformationOptions,
} from "./constants_property_management";

// New styled components for grid replacement
const StyledContainer = styled("div")({
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    margin: "0 -12px",
});

const StyledColumn = styled("div")(({ width }) => ({
    width: width || "100%",
    padding: "0 12px",
    boxSizing: "border-box",
}));

const StyledRow = styled("div")({
    display: "flex",
    flexWrap: "wrap",
    margin: "0 -8px",
    width: "100%",
    marginBottom: "16px",
});

const StyledCell = styled("div")(({ width }) => ({
    width: width || "100%",
    padding: "0 8px",
    boxSizing: "border-box",
}));

const AccountingForm = (props) => {
    const { clearErrorByField, data, disableInputs, errors, handleInput, sideBySide } = props;
    const [softwareLicenseReimbursed, setSoftwareLicenseReimbursed] = useState(
        data.softwareLicenseReimbursedAmount ? true : false
    );

    const handleRealEstateTaxesPaidBy = (e) => {
        handleInput(e.target.id, e.target.value);
        if (e.target.value === "Kidder") {
            handleInput("taxOwnershipInformation", "");
        }
    };

    const handleSoftwareLicenseReimbursed = (e) => {
        setSoftwareLicenseReimbursed(!softwareLicenseReimbursed);
        if (!e.target.checked) {
            handleInput("softwareLicenseReimbursedAmount", "");
            handleInput("softwareLicenseReimbursedFrequency", undefined);
            clearErrorByField("softwareLicenseReimbursedAmount");
            clearErrorByField("softwareLicenseReimbursedFrequency");
        }
    };

    return (
        <React.Fragment>
            <StyledContainer>
                <StyledColumn width={sideBySide ? "50%" : "100%"}>
                    <StyledRow>
                        <StyledCell width="50%">
                            <BSSelect
                                id="accountingMethod"
                                label="Accounting Method"
                                placeholder="Select"
                                value={get(data, "accountingMethod", "")}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                options={[
                                    {
                                        value: "",
                                        label: "Select",
                                        disabled: true,
                                    },
                                    ...accountingMethodTypes,
                                ]}
                                disabled={disableInputs}
                            />
                        </StyledCell>
                        <StyledCell width="50%">
                            <BSSelect
                                id="accountingSoftwareType"
                                label="Accounting Software System"
                                placeholder="Select"
                                value={get(data, "accountingSoftwareType", "")}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                options={[
                                    {
                                        value: "",
                                        label: "Select",
                                        disabled: true,
                                    },
                                    ...accountingSoftwareSystemTypes,
                                ]}
                                disabled={disableInputs}
                            />
                        </StyledCell>
                    </StyledRow>

                    <StyledRow>
                        <StyledCell>
                            <BSInput
                                id="chartOfAccounts"
                                label="Chart Of Accounts"
                                placeholder="Enter chart of accounts"
                                value={get(data, "chartOfAccounts", "")}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                disabled={disableInputs}
                            />
                        </StyledCell>
                    </StyledRow>

                    <StyledRow>
                        <StyledCell>
                            <BSInput
                                id="bankingInstitution"
                                label="Banking Institution"
                                placeholder="Enter banking institution"
                                value={get(data, "bankingInstitution", "")}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                disabled={disableInputs}
                            />
                        </StyledCell>
                    </StyledRow>

                    <StyledRow>
                        <StyledCell>
                            <BSInput
                                id="bankAccountId"
                                label="Bank Account Id"
                                placeholder="Enter bank account ID"
                                value={get(data, "bankAccountId", "")}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                disabled={disableInputs}
                            />
                        </StyledCell>
                    </StyledRow>
                </StyledColumn>

                {/* right column */}
                <StyledColumn width={sideBySide ? "50%" : "100%"}>
                    <StyledRow>
                        <StyledCell width="33.33%">
                            <BSSelect
                                id="accountingCutoffDayOfMonth"
                                label="Accounting Cutoff Date"
                                placeholder="Enter date"
                                value={get(data, "accountingCutoffDayOfMonth", "")}
                                error={get(errors, "accountingCutoffDayOfMonth", null)}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                disabled={disableInputs}
                                options={dateOrdinalOptions}
                            />
                        </StyledCell>
                        <StyledCell width="33.33%">
                            <BSSelect
                                id="reportDueDayOfMonth"
                                label="Monthly Report Due Date"
                                placeholder="Select"
                                value={get(data, "reportDueDayOfMonth", "")}
                                error={get(errors, "reportDueDayOfMonth", null)}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                options={dateOrdinalOptions}
                                disabled={disableInputs}
                            />
                        </StyledCell>
                        <StyledCell width="33.33%">
                            <BSInput
                                id="budgetDueMonthDay"
                                label="Budget Due Date"
                                placeholder="MM/DD"
                                value={get(data, "budgetDueMonthDay", "")}
                                error={get(errors, "budgetDueMonthDay", null)}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                disabled={disableInputs}
                                options={dateOrdinalOptions}
                            />
                        </StyledCell>
                    </StyledRow>

                    <StyledRow>
                        <StyledCell width={get(data, "realEstateTaxesPaidBy", "") === "Other" ? "50%" : "100%"}>
                            <BSSelect
                                id="realEstateTaxesPaidBy"
                                label="Real Estate Taxes Paid By"
                                value={get(data, "realEstateTaxesPaidBy", "")}
                                error={get(errors, "realEstateTaxesPaidBy", null)}
                                onChange={handleRealEstateTaxesPaidBy}
                                disabled={disableInputs}
                                options={realEstateTaxesPaidByOptions}
                            />
                        </StyledCell>
                        {get(data, "realEstateTaxesPaidBy", "") === "Other" && (
                            <StyledCell width="50%">
                                <BSSelect
                                    id="taxOwnershipInformation"
                                    label="Tax Ownership Information"
                                    value={get(data, "taxOwnershipInformation", "")}
                                    error={get(errors, "taxOwnershipInformation", null)}
                                    onChange={(e) => handleInput(e.target.id, e.target.value)}
                                    disabled={disableInputs}
                                    options={taxOwnershipInformationOptions}
                                />
                            </StyledCell>
                        )}
                    </StyledRow>

                    <StyledRow>
                        <StyledCell width="33.33%" style={{ marginTop: "12px" }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={data.tpt}
                                        onChange={(e, checked) => handleInput(e.target.value, checked)}
                                        value="tpt"
                                        disabled={disableInputs}
                                        color="primary"
                                    />
                                }
                                label="TPT"
                            />
                        </StyledCell>
                        <StyledCell width="33.33%">
                            <BSInput
                                id="softwareLicenseReimbursedAmount"
                                label="S/W Lic. Reimbursed"
                                placeholder="$"
                                value={get(data, "softwareLicenseReimbursedAmount", null)}
                                error={get(errors, `softwareLicenseReimbursedAmount`, null)}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                disabled={!softwareLicenseReimbursed || disableInputs}
                                checkbox={true}
                                checkboxVal={"softwareLicenseReimbursed"}
                                disableCheckbox={disableInputs}
                                onChangeCheckbox={handleSoftwareLicenseReimbursed}
                                checked={softwareLicenseReimbursed}
                            />
                        </StyledCell>
                        <StyledCell width="33.33%">
                            <BSSelect
                                id="softwareLicenseReimbursedFrequency"
                                value={get(data, "softwareLicenseReimbursedFrequency", "")}
                                error={get(errors, "softwareLicenseReimbursedFrequency", null)}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                disabled={!softwareLicenseReimbursed || disableInputs}
                                options={softwareLicenseReimbursedFrequencyOptions}
                            />
                        </StyledCell>
                    </StyledRow>

                    <StyledRow>
                        <StyledCell>
                            <BSSelect
                                id="kidderMathewsCompanyKey"
                                label="Kidder Mathews Company"
                                placeholder="Select a company"
                                value={get(data, "kidderMathewsCompanyKey", "")}
                                onChange={(e) => handleInput(e.target.id, e.target.value)}
                                disabled={disableInputs}
                                options={[
                                    {
                                        value: "",
                                        label: "Select",
                                        disabled: false,
                                    },
                                    {
                                        value: "218011",
                                        label: "Kidder Mathews, LLC",
                                    },
                                    {
                                        value: "415674",
                                        label: "Kidder Mathews of California, Inc.",
                                    },
                                ]}
                            />
                        </StyledCell>
                    </StyledRow>
                </StyledColumn>
            </StyledContainer>
        </React.Fragment>
    );
};

export default AccountingForm;
